<template>
    <div class="bg-white shadow rounded mx-auto relative p-40 mb-40">
        <div class="flex items-center gap-8 mb-18 text-black">
            <p class="font-semibold text-body-sm">
                {{ $t('jobs.create.share_job.header.link') }}
                <span>
                    {{ translated(job.title, locale, true) }}
                    {{ location }}
                </span>
            </p>
            <div @click="openPreview">
                <simple-button
                    icon="open-in-new"
                    look="icon"
                    small
                    @click="openPreview"
                />
            </div>
        </div>

        <h2 class="text-body">{{ $t('jobs.publish_your_vacancy') }}</h2>

        <div class="rounded border border-primary-20 overflow-hidden mt-32">
            <div class="border-b border-grey-lighter">
                <div class="pt-16 pl-24 pr-16 bg-white overflow-hidden">
                    <div class="flex items-center mb-16">
                        <toggle
                            :checked="publication.published"
                            :disabled="notAvailableForPublish"
                            class="mr-auto"
                            @change="setPublishedLanguages">
                            {{ $t('jobs.publish_job') }}
                        </toggle>
                    </div>
                </div>

                <div v-show="publication.published" class="py-32 px-24 bg-primary-5 border-t border-primary-20 overflow-hidden space-y-32">

                    <banner
                    class="w-full"
                    :description="$t('jobs.no_job_title_alert.description')"
                    inline
                    :one-line="false"
                    :title="$t('jobs.no_job_title_alert.title', {languages: noTitleForLang})"
                    v-if="noTitleForLang.length || notAvailableForPublish"
                    warning
                />

                <div>
                        <toggle v-model="publication.schedule">{{ $t('jobs.create.headers.start_and_end_date_help') }}</toggle>

                        <div v-show="publication.schedule" class="pt-16 flex items-center">
                            <div class="w-1/2 mr-32">
                                <datepicker v-model="publication.start_date" time>
                                    {{ $t('jobs.create.start_date') }}
                                </datepicker>
                            </div>
                            <div class="w-1/2">
                                <datepicker v-model="publication.end_date" time>
                                    {{ $t('jobs.create.end_date') }}
                                </datepicker>
                            </div>
                        </div>
                    </div>

                    <div v-if="currentCompany.jobsite.active_languages.length > 1">
                        <div class="text-body font-semibold mb-16">
                            {{ $t('jobs.create.headers.active_languages_help') }}
                        </div>

                        <template v-for="lang in currentCompany.jobsite.active_languages" :key="'active-in-' + lang">
                            <toggle :checked="noTitleForLang.includes(lang) ? false : publication.active[lang]"
                                    :disabled="noTitleForLang.includes(lang)"
                                    class="mb-8"
                                    @change="publication.active[lang] = $event"
                            >{{ $t('jobs.create.publish_language', {language: $t('languages.' + lang + '.adjective')}) }}
                        </toggle>
                    </template>
                </div>
            </div>
        </div>

            <div class="p-40">
                <div class="mb-32">
                    <h3 class="text-body">{{ $t('jobs.steps.publish.usp_section.title') }}</h3>
                    <p>{{ $t('jobs.steps.publish.usp_section.description') }}</p>
                </div>

                <div class="grid grid-cols-5 gap-24">
                    <div v-for="usp in usps">
                        <div class="flex justify-center items-center w-56 h-56 bg-secondary-20 rounded-full mb-16">
                            <svg-icon :type="usp.icon" class="w-32 h-32 text-secondary"></svg-icon>
                        </div>
                        <p class="font-semibold">{{ usp.title }}</p>
                        <p>{{ usp.description }}</p>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex justify-end pt-32">
            <template v-if="$route.name === 'jobs.create'">
                <simple-button
                    id="create-job"
                    :loading="publication.submitting"
                    display="primary"
                    icon="save"
                    @click="$emit('save', {todo:'next', form: publication})"
                >{{ $t('jobs.create.finish') }}
                </simple-button>
            </template>
            <template v-else>
                <div class="flex items-center justify-end">
                    <simple-button
                        :loading="publication.submitting"
                        class="mr-8"
                        id="save-and-close"
                        look="text"
                        @click="$emit('save', {todo:'close',form: publication})">
                        {{ $t('jobs.save_and_close') }}
                    </simple-button>
                    <simple-button
                        :loading="publication.submitting"
                        display="secondary"
                        look="primary"
                        icon="save"
                        id="save-and-next"
                        @click="$emit('save', {todo:'next',form: publication})">
                        {{ $t('jobs.save_and_next') }}
                    </simple-button>
                </div>
            </template>
        </div>

    </div>
</template>

<script>
import SimpleButton from './SimpleButton.vue';
import Job from '../../models/Job';
import {mapState} from 'pinia';
import {useMainStore} from '../../state/store';
import SvgIcon from './SvgIcon.vue';
import PopupModalConfirm from './PopupModalConfirm.vue';
import DatePicker from '../forms/DatePicker.vue';
import Toggle from '../forms/Toggle.vue';
import Form from '../../etc/forms/Form';
import moment from 'moment';
import Banner from "./Banner.vue";
import capitalize from "../../mixins/capitalize";
import {ADD_NOTIFICATION} from "../../state/mutation-types";

export default {
    components: {
        Banner,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'datepicker': DatePicker,
        'toggle': Toggle,
    },
    mixins: [capitalize],
    props: {
        job: {
            required: true,
            type: Job,
        },
    },
    data: function () {
        return {
            publication: this.generatePublicationForm(),
            usps: [
                {
                    icon: 'rocket-launch',
                    title: this.$t('jobs.steps.publish.usps.title.socials'),
                    description: this.$t('jobs.steps.publish.usps.description.socials')
                },
                {
                    icon: 'campaigns',
                    title: this.$t('jobs.steps.publish.usps.title.jobboard'),
                    description: this.$t('jobs.steps.publish.usps.description.jobboards')
                },
                {
                    icon: 'extension',
                    title: this.$t('jobs.steps.publish.usps.title.integration'),
                    description: this.$t('jobs.steps.publish.usps.description.integrations')
                },
                {
                    icon: 'interim',
                    title: this.$t('jobs.steps.publish.usps.title.external_partners'),
                    description: this.$t('jobs.steps.publish.usps.description.external_partners')
                },
                {
                    icon: 'link',
                    title: this.$t('jobs.steps.publish.usps.title.links'),
                    description: this.$t('jobs.steps.publish.usps.description.links')
                },
            ]
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'locale']),
        location() {
            return (this.job.active_location && this.job.active_location.city) ? `- ${this.job.active_location.city}` : null;
        },
        noTitleForLang() {
            const langs = Object.entries(this.job.title)
                .filter(([key, value]) => value === '')
                .map(lang => lang[0]);
            return langs.join(', ');
        },
        notAvailableForPublish() {
            return Object.keys(this.job.active).length === this.noTitleForLang.length;
        },
        jobsite_locale() {
            if (this.currentCompany.jobsite.active_languages.includes(this.locale)) {
                return this.locale;
            }

            if (this.activeLocale.length && this.activeLocale[0]['value']) {
                return this.activeLocale[0]['value'];
            }

            return this.locale;
        },
        activeLocale() {
            let activeLocale = [];

            for (let locale in this.currentCompany.jobsite.active_languages) {
                activeLocale.push(this.currentCompany.jobsite.active_languages[locale]);
            }

            return activeLocale.map(locale => ({
                label: this.$t('languages.' + locale + '.title'),
                value: locale,
            }));
        },
    },
    methods: {
        openPreview() {
            if (!this.job.url[this.jobsite_locale]) {
                return this[ADD_NOTIFICATION]({
                    type: 'error',
                    message: this.$t('jobs.preview_blocked', {lang: this.$t('languages.' + this.jobsite_locale + '.title')}),
                });
            }

            //show with no hash
            let url = this.job.url[this.jobsite_locale];

            //check is jobsite is published in this language.
            if (!this.job.published || !this.job.active[this.jobsite_locale] || (this.job.schedule && moment().utc().isBefore(moment.utc(this.job.start_date)))) {
                url += '?previewHash=' + this.job.previewHash;
            }

            window.open(url, '_blank').focus();
        },
        generatePublicationForm() {
            return new Form([
                {name: 'published', value: this.job.published},
                {name: 'schedule', value: this.job.schedule},
                {
                    name: 'start_date',
                    value: this.job.start_date,
                    accessor: value => value ? moment.utc(value) : null,
                },
                {
                    name: 'end_date',
                    value: this.job.end_date,
                    accessor: value => value ? moment.utc(value) : null,
                },
                {name: 'active', value: this.job.active, translatable: true},
            ]);
        },
        setPublishedLanguages(val) {
            this.publication.published = val;

            if (val) {
                // Let's check if at least 1 language is active. If not, we'll activate all languages.
                let published = false;

                for (let lang in this.publication.active && this.currentCompany.jobsite.active_languages.length > 1) {
                    if (this.publication.active.hasOwnProperty(lang) && this.publication.active[lang]) {
                        published = true;
                    }
                }

                if (!published) {
                    for (let lang in this.publication.active) {
                        if (this.publication.active.hasOwnProperty(lang)) {
                            this.publication.active[lang] = true;
                        }
                    }
                }
            }
        },
    },
    watch: {
        //When one toggle changes check whole list again
        //Also set published true/false
        'publication.active': {
            deep: true,
            handler(val) {
                let published = false;

                for (let lang in val) {
                    if (val.hasOwnProperty(lang) && this.noTitleForLang.includes(lang)) {
                        this.publication.active[lang] = false;
                    }

                    if (val.hasOwnProperty(lang) && val[lang]) {
                        published = true;
                    }
                }

                this.publication.published = published;
            },
        },
    },
    mounted() {
        //When mounted Check for active languages
        if (this.publication.active) {
            for (let lang in this.publication.active) {
                if (this.publication.active.hasOwnProperty(lang) && this.noTitleForLang.includes(lang)) {
                    this.publication.active[lang] = false;
                }
            }
        }
    }
};
</script>
